import { Component } from '@angular/core';
import { Store } from '../../../core/models';
import { Store as NgrxStore } from '@ngrx/store';
import { AppState } from '../../../app-state';
import { AuthorizationAwareComponent } from '../../../core/authorization-aware-component';
import { DetailState, selectDetailState } from '../../detail-state';
import { filter, take, tap, timeout, catchError } from 'rxjs/operators';
import { BannerComparison } from '../../../core/banner/banner-comparison';
import { BannerAnalysisDataService } from '../../../core/services/banner-analysis-data.service';
import { of } from 'rxjs';

@Component({
  standalone: false,
  selector: 'mtn-store-banner-analysis-panel',
  templateUrl: './store-banner-analysis-panel.component.html',
  styleUrls: ['./store-banner-analysis-panel.component.scss']
})
export class StoreBannerAnalysisPanelComponent extends AuthorizationAwareComponent {

  public readonly PANEL_DESCRIPTION = 'Compare the entire banner\'s performance in the market. How does this store\'s banner compare to all other banners with a presence in the market?';

  comparisonSalesSqft: BannerComparison;
  comparisonSalesVolume: BannerComparison;
  comparisonStoreCount: BannerComparison;
  store: Store;
  marketName: any;
  isLoading = false;
  hasDataLoadError = false;

  constructor(protected ngrxStore: NgrxStore<AppState>,
              private bannerAnalysisDataService: BannerAnalysisDataService) {
    super(ngrxStore);
  }

  onAuthorizationChange(): void {
  }

  onAuthorizationInit(): void {
    this.subscribeToDetailState();
  }

  private subscribeToDetailState(): void {
    this.isLoading = true;
    this.hasDataLoadError = false;
    
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .pipe(
          filter((state: DetailState) => {
            // Must have store
            if (!state.store) return false;
            
            // If store has no banner, we can proceed immediately
            if (!state.store.banner?.uuid) return true;
            
            // If store has a banner but no market area, proceed (will show insufficient data)
            if (!state.marketArea || !state.marketArea.features?.length) return true;
            
            // If store has a banner and market area, wait for all banner comparisons to load
            return !!(state.bannerComparisonSalesSqft && 
                     state.bannerComparisonSalesVolume && 
                     state.bannerComparisonStoreCount);
          }),
          timeout(10000), // 10 second timeout to prevent infinite loading
          take(1),
          tap((state: DetailState) => {
            this.store = state.store;
            if (state.marketArea && state.marketArea.getFeature()) {
              const feature = state.marketArea.getFeature();
              this.marketName = feature?.properties ? feature.properties['name'] : null;
            }
            
            // Set comparisons if available
            this.comparisonSalesSqft = state.bannerComparisonSalesSqft;
            this.comparisonSalesVolume = state.bannerComparisonSalesVolume;
            this.comparisonStoreCount = state.bannerComparisonStoreCount;

            // Update service with banner analysis data
            this.bannerAnalysisDataService.updateBannerAnalysis({
              bannerName: this.store?.banner?.name || 'Unknown Banner',
              marketName: this.marketName,
              salesSqft: this.comparisonSalesSqft,
              salesVolume: this.comparisonSalesVolume,
              storeCount: this.comparisonStoreCount
            });

            // Stop loading
            this.isLoading = false;
          }),
          catchError((error) => {
            console.error('Error or timeout loading banner analysis:', error);
            this.hasDataLoadError = true;
            this.isLoading = false;
            return of(null);
          })
        )
        .subscribe()
    );
  }
}
